import React from "react";
import PropTypes from "prop-types";
import {
  AlertTriangle,
  Badge,
  Button,
  CheckCircle,
  Clock,
  Lock01,
  Plus,
  Tooltip,
  XCircle,
} from "@opendate-app/setlist";
import {
  clockLabel,
  columnDayLabel,
  hoursLabel,
  percentLabel,
  rateLabel,
  shortName,
  wholeDollars,
  shiftColumnId,
} from "./scheduleFormatters";
import { useStaffingScheduleContext } from "./StaffingScheduleContext";

const STATUS_BADGES = {
  confirm: { label: "Confirm", color: "success", icon: CheckCircle },
  hold: { label: "Hold", color: "gray", icon: Lock01 },
  postponed: { label: "Postponed", color: "warning", icon: XCircle },
  canceled: { label: "Canceled", color: "error", icon: XCircle },
};

const cellBackground = (status) => {
  if (status === "hold") return "var(--sl-bg-secondary)";
  if (status === "postponed" || status === "canceled")
    return "var(--sl-bg-warning-primary)";
  return "transparent";
};

export const cardStyle = (shift) => {
  if (!shift.staff_member_id)
    return {
      background: "var(--sl-bg-warning-primary)",
      border: "1px dashed var(--sl-border-warning)",
    };
  if (shift.state === "draft")
    return {
      background: "var(--sl-bg-secondary)",
      border: "1px dashed var(--sl-border-primary)",
    };
  return {
    background: "var(--sl-bg-primary)",
    border: "1px solid var(--sl-border-secondary)",
  };
};

export const sortShifts = (shifts) =>
  [...shifts].sort(
    (first, second) =>
      Number(!first.staff_member_id) - Number(!second.staff_member_id) ||
      new Date(first.starts_at) - new Date(second.starts_at),
  );

export const ColumnHeader = ({
  column,
  shifts,
  cancelling,
  onCancelShifts,
  changingTimes,
  onChangeShowTimes,
}) => {
  const { permissions } = useStaffingScheduleContext();
  const linkedCount = column.linked_shift_count;
  const canCancel =
    permissions.manageSchedule &&
    shifts.length === linkedCount &&
    shifts.every((shift) => shift.can_edit);
  const { moved } = column;
  const canChangeTimes =
    permissions.manageSchedule && Boolean(moved?.can_change);
  const shiftWord = linkedCount === 1 ? "shift" : "shifts";
  const badge = STATUS_BADGES[column.status];
  const muted = !["confirm", "day"].includes(column.status);
  const roomLine = [
    column.room_names.join(", "),
    column.capacity ? `${column.capacity.toLocaleString("en-US")} cap` : null,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <div
      style={{
        padding: 12,
        background: ["postponed", "canceled"].includes(column.status)
          ? "var(--sl-bg-warning-primary)"
          : "transparent",
        borderLeft: "1px solid var(--sl-border-secondary)",
      }}
    >
      <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
        <span
          style={{
            fontSize: 12,
            lineHeight: "18px",
            color: "var(--sl-text-tertiary)",
          }}
        >
          {columnDayLabel(column.date)}
        </span>
        {badge && (
          <Badge size="sm" color={badge.color} icon={badge.icon}>
            {badge.label}
          </Badge>
        )}
        {moved && (
          <Badge size="sm" color="warning" icon={Clock}>
            Show moved {moved.label}
          </Badge>
        )}
      </div>
      <div
        style={{
          fontSize: 16,
          lineHeight: "24px",
          fontWeight: 600,
          marginTop: 4,
          color: muted ? "var(--sl-text-quaternary)" : "var(--sl-text-primary)",
        }}
      >
        {column.title}
      </div>
      {roomLine && (
        <div
          style={{
            fontSize: 13,
            lineHeight: "18px",
            color: "var(--sl-text-tertiary)",
          }}
        >
          {roomLine}
        </div>
      )}
      <div
        style={{
          fontSize: 13,
          lineHeight: "18px",
          color: "var(--sl-text-tertiary)",
        }}
      >
        {column.day
          ? "Shifts with no show linked"
          : [
              `Doors ${clockLabel(column.doors_clock)}`,
              `Show ${clockLabel(column.show_clock)}`,
              `End ${clockLabel(column.end_clock)}`,
            ].join(" · ")}
      </div>
      {column.status === "hold" && (
        <div
          style={{
            fontSize: 12,
            lineHeight: "18px",
            color: "var(--sl-text-quaternary)",
            marginTop: 4,
          }}
        >
          Possible show. Not staffed until confirmed.
        </div>
      )}
      {["postponed", "canceled"].includes(column.status) && linkedCount > 0 && (
        <div
          style={{
            marginTop: 6,
            padding: 8,
            borderRadius: "var(--sl-radius-md)",
            background: "var(--sl-bg-primary)",
            border: "1px solid var(--sl-border-warning)",
          }}
        >
          <div
            style={{
              fontSize: 12,
              lineHeight: "18px",
              color: "var(--sl-text-warning)",
              marginBottom: 6,
            }}
          >
            {linkedCount} linked {shiftWord}. Staff aren&apos;t notified until
            you act.
          </div>
          {canCancel && (
            <Button
              variant="secondary"
              size="sm"
              disabled={cancelling}
              onClick={() => onCancelShifts(column, shifts)}
            >
              Cancel {linkedCount} {shiftWord}
            </Button>
          )}
        </div>
      )}
      {moved && (
        <div
          style={{
            marginTop: 6,
            padding: 8,
            borderRadius: "var(--sl-radius-md)",
            background: "var(--sl-bg-primary)",
            border: "1px solid var(--sl-border-warning)",
          }}
        >
          <div
            style={{
              fontSize: 12,
              lineHeight: "18px",
              color: "var(--sl-text-warning)",
              marginBottom: canChangeTimes ? 6 : 0,
            }}
          >
            {moved.shift_count} published{" "}
            {moved.shift_count === 1 ? "shift is" : "shifts are"} on the old
            times.
          </div>
          {canChangeTimes && (
            <div className="d-flex flex-wrap" style={{ gap: 6 }}>
              <Button
                variant="secondary"
                size="sm"
                disabled={changingTimes}
                onClick={() => onChangeShowTimes(column, "match")}
              >
                Shift times to match
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={changingTimes}
                onClick={() => onChangeShowTimes(column, "keep")}
              >
                Keep times
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

ColumnHeader.propTypes = {
  column: PropTypes.object.isRequired,
  shifts: PropTypes.arrayOf(PropTypes.object).isRequired,
  cancelling: PropTypes.bool.isRequired,
  onCancelShifts: PropTypes.func.isRequired,
  changingTimes: PropTypes.bool.isRequired,
  onChangeShowTimes: PropTypes.func.isRequired,
};

const PositionDot = ({ color }) => (
  <span
    style={{
      width: 8,
      height: 8,
      borderRadius: "50%",
      background: color || "var(--sl-fg-brand)",
      flexShrink: 0,
    }}
  />
);

PositionDot.propTypes = { color: PropTypes.string };

const ShiftCard = ({ shift, position, roomName, onOpen }) => {
  const open = !shift.staff_member_id;
  let label = open
    ? "Open shift"
    : shortName(shift.staff_member_first_name, shift.staff_member_last_name);
  if (position) label = position.name;
  return (
    <button
      type="button"
      onClick={() => onOpen(shift)}
      className="w-100 text-left"
      style={{
        ...cardStyle(shift),
        borderRadius: "var(--sl-radius-md)",
        padding: "8px 10px",
        cursor: "pointer",
      }}
    >
      <div
        className="d-flex justify-content-between align-items-start"
        style={{ gap: 6 }}
      >
        <span
          className="d-flex align-items-center"
          style={{
            gap: 6,
            fontSize: 14,
            lineHeight: "20px",
            fontWeight: 500,
            color: open ? "var(--sl-text-warning)" : "var(--sl-text-primary)",
          }}
        >
          {position && <PositionDot color={position.color_hex} />}
          {label}
        </span>
        {shift.warnings.length > 0 && (
          <Tooltip title={shift.warnings.join("\n")}>
            <AlertTriangle
              width={16}
              height={16}
              style={{ color: "var(--sl-fg-warning)" }}
              aria-label="Has warnings"
            />
          </Tooltip>
        )}
      </div>
      <div
        className="d-flex justify-content-between align-items-center"
        style={{ gap: 6 }}
      >
        <span
          style={{
            fontSize: 13,
            lineHeight: "18px",
            color: "var(--sl-text-tertiary)",
          }}
        >
          {clockLabel(shift.start_clock)}–{clockLabel(shift.end_clock)}
        </span>
        {(shift.state === "draft" || shift.pending_change) && (
          <span
            style={{
              fontSize: 11,
              lineHeight: "16px",
              fontWeight: 600,
              letterSpacing: "0.04em",
              color: "var(--sl-text-quaternary)",
            }}
          >
            {shift.state === "draft" ? "DRAFT" : "EDITED"}
          </span>
        )}
      </div>
      {roomName && (
        <div
          style={{
            fontSize: 12,
            lineHeight: "18px",
            color: "var(--sl-text-tertiary)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {roomName}
        </div>
      )}
    </button>
  );
};

ShiftCard.propTypes = {
  shift: PropTypes.object.isRequired,
  position: PropTypes.object,
  roomName: PropTypes.string,
  onOpen: PropTypes.func.isRequired,
};

// One row per person who holds a visible position, plus anyone with a shift this
// week whose position they no longer hold, so no shift drops off the grid.
const staffRows = (schedule) => {
  const people = new Map();
  schedule.rows.forEach((row) =>
    (schedule.staffOptions[row.id] || []).forEach((person) => {
      if (!people.has(person.id))
        people.set(person.id, { ...person, positions: [] });
      people.get(person.id).positions.push(row);
    }),
  );
  schedule.shifts.forEach((shift) => {
    if (!shift.staff_member_id || people.has(shift.staff_member_id)) return;
    people.set(shift.staff_member_id, {
      id: shift.staff_member_id,
      name: [shift.staff_member_first_name, shift.staff_member_last_name]
        .filter(Boolean)
        .join(" "),
      scheduled_minutes: null,
      positions: [],
    });
  });
  return Array.from(people.values()).sort((first, second) =>
    first.name.localeCompare(second.name),
  );
};

const RowLabel = ({ children, background, staffRow }) => (
  <div
    data-staff-row={staffRow}
    style={{
      padding: 12,
      borderTop: "1px solid var(--sl-border-secondary)",
      position: "sticky",
      left: 0,
      background: background || "var(--sl-bg-primary)",
      zIndex: 1,
      minWidth: 0,
    }}
  >
    {children}
  </div>
);

RowLabel.propTypes = {
  children: PropTypes.node.isRequired,
  background: PropTypes.string,
  staffRow: PropTypes.string,
};

const subtleText = {
  fontSize: 13,
  lineHeight: "18px",
  color: "var(--sl-text-tertiary)",
};

const PositionLabel = ({ row, narrow }) => (
  <>
    <div className="d-flex align-items-center" style={{ gap: 8 }}>
      <PositionDot color={row.color_hex} />
      <span
        style={{
          fontSize: 15,
          lineHeight: "22px",
          fontWeight: 500,
          color: "var(--sl-text-primary)",
        }}
      >
        {row.name}
      </span>
    </div>
    <div style={{ ...subtleText, paddingLeft: 16 }}>
      {[
        !narrow &&
          (row.volunteer
            ? "Volunteer"
            : row.default_hourly_rate && rateLabel(row.default_hourly_rate)),
        `${row.staff_count} staff`,
      ]
        .filter(Boolean)
        .join(" · ")}
    </div>
  </>
);

PositionLabel.propTypes = {
  row: PropTypes.object.isRequired,
  narrow: PropTypes.bool,
};

const PersonLabel = ({ person, overtimeAfterMinutes, narrow }) => {
  const minutes = person.scheduled_minutes;
  const overtime =
    minutes != null &&
    overtimeAfterMinutes != null &&
    minutes > overtimeAfterMinutes;
  const [first, ...rest] = person.name.split(" ");
  let hours = null;
  if (minutes != null) {
    hours = narrow
      ? `${hoursLabel(minutes)} h`
      : `${hoursLabel(minutes)} h this week`;
    if (overtime)
      hours += narrow
        ? " · OT"
        : ` · overtime after ${hoursLabel(overtimeAfterMinutes)} h`;
  }
  return (
    <>
      <div
        style={{
          fontSize: 15,
          lineHeight: "22px",
          fontWeight: 500,
          color: "var(--sl-text-primary)",
        }}
      >
        {narrow ? shortName(first, rest.pop()) : person.name}
      </div>
      {!narrow && person.positions.length > 0 && (
        <div style={subtleText}>
          {person.positions.map((position) => position.name).join(", ")}
        </div>
      )}
      {hours && (
        <div
          className="d-flex align-items-center"
          style={{
            ...subtleText,
            gap: 4,
            fontWeight: 600,
            color: overtime
              ? "var(--sl-text-warning)"
              : "var(--sl-text-secondary)",
          }}
        >
          {overtime && (
            <AlertTriangle
              width={14}
              height={14}
              style={{ color: "var(--sl-fg-warning)", flexShrink: 0 }}
              aria-hidden="true"
            />
          )}
          {hours}
        </div>
      )}
    </>
  );
};

PersonLabel.propTypes = {
  person: PropTypes.object.isRequired,
  overtimeAfterMinutes: PropTypes.number,
  narrow: PropTypes.bool,
};

const ScheduleGrid = ({
  view,
  narrow,
  columns,
  room,
  onOpenShift,
  onAddShift,
  cancellingEventId,
  onCancelShifts,
  changingTimesEventId,
  onChangeShowTimes,
}) => {
  const { schedule, permissions } = useStaffingScheduleContext();
  const labelWidth = narrow ? 108 : 180;
  const cellWidth = narrow ? 152 : 176;
  const gridTemplateColumns = `${labelWidth}px repeat(${columns.length}, minmax(${cellWidth}px, 1fr))`;
  const minWidth = labelWidth + columns.length * cellWidth;
  const byStaff = view === "staff";
  const positionsById = Object.fromEntries(
    schedule.rows.map((row) => [row.id, row]),
  );
  const columnShifts = (column) =>
    schedule.shifts.filter((shift) => shiftColumnId(shift) === column.id);
  // Only a show across several rooms names the room on its shifts; the column header already names a lone room.
  const roomName = (column, shift) =>
    column.room_ids.length > 1
      ? column.room_names[column.room_ids.indexOf(shift.room_id)]
      : undefined;
  // A room the show no longer uses counts as no room, so the shift never drops off every tab.
  const inRoom = (column, shift) =>
    room == null ||
    !column.room_ids.includes(shift.room_id) ||
    shift.room_id === room;

  const rows = byStaff
    ? [
        {
          key: "open",
          label: (
            <>
              <div
                style={{
                  fontSize: 15,
                  lineHeight: "22px",
                  fontWeight: 600,
                  color: "var(--sl-text-warning)",
                }}
              >
                Open shifts
              </div>
              <div style={subtleText}>
                {
                  columns
                    .flatMap((column) =>
                      columnShifts(column).filter((shift) =>
                        inRoom(column, shift),
                      ),
                    )
                    .filter((shift) => !shift.staff_member_id).length
                }{" "}
                to fill
              </div>
            </>
          ),
          background: "var(--sl-bg-warning-primary)",
          matches: (shift) => !shift.staff_member_id,
          addPosition: schedule.rows.find((row) => row.can_manage),
        },
        ...staffRows(schedule).map((person) => ({
          key: String(person.id),
          label: (
            <PersonLabel
              person={person}
              overtimeAfterMinutes={schedule.weeklyOvertimeAfterMinutes}
              narrow={narrow}
            />
          ),
          matches: (shift) => shift.staff_member_id === person.id,
          addPosition: person.positions.find((position) => position.can_manage),
          person,
        })),
      ]
    : schedule.rows.map((row) => ({
        key: String(row.id),
        label: <PositionLabel row={row} narrow={narrow} />,
        matches: (shift) => shift.staff_position_id === row.id,
        addPosition: row.can_manage ? row : null,
      }));

  return (
    <div
      style={{
        border: "1px solid var(--sl-border-secondary)",
        borderRadius: "var(--sl-radius-xl)",
        background: "var(--sl-bg-primary)",
        overflowX: "auto",
      }}
    >
      <div style={{ display: "grid", gridTemplateColumns, minWidth }}>
        <div
          className="d-flex align-items-end"
          style={{
            padding: 12,
            position: "sticky",
            left: 0,
            background: "var(--sl-bg-primary)",
            zIndex: 1,
          }}
        >
          <span
            style={{
              fontSize: 12,
              lineHeight: "18px",
              color: "var(--sl-text-tertiary)",
            }}
          >
            {byStaff ? "Staff" : "Position"}
          </span>
        </div>
        {columns.map((column) => (
          <ColumnHeader
            key={column.id}
            column={column}
            shifts={columnShifts(column)}
            cancelling={cancellingEventId != null}
            onCancelShifts={onCancelShifts}
            changingTimes={changingTimesEventId != null}
            onChangeShowTimes={onChangeShowTimes}
          />
        ))}

        {rows.map((row) => (
          <React.Fragment key={row.key}>
            <RowLabel
              background={row.background}
              staffRow={byStaff ? row.key : undefined}
            >
              {row.label}
            </RowLabel>
            {columns.map((column) => (
              <div
                key={column.id}
                className="d-flex flex-column"
                style={{
                  padding: 8,
                  gap: 8,
                  borderTop: "1px solid var(--sl-border-secondary)",
                  borderLeft: "1px solid var(--sl-border-secondary)",
                  background: cellBackground(column.status),
                }}
              >
                {sortShifts(
                  columnShifts(column).filter(
                    (shift) => row.matches(shift) && inRoom(column, shift),
                  ),
                ).map((shift) => (
                  <ShiftCard
                    key={shift.id}
                    shift={shift}
                    position={
                      byStaff ? positionsById[shift.staff_position_id] : null
                    }
                    roomName={roomName(column, shift)}
                    onOpen={onOpenShift}
                  />
                ))}
                {permissions.manageSchedule && row.addPosition && (
                  <Button
                    variant="tertiary"
                    size="sm"
                    icon={Plus}
                    data-add-for={row.person?.id}
                    onClick={() =>
                      onAddShift(row.addPosition, column, row.person)
                    }
                  >
                    Add
                  </Button>
                )}
              </div>
            ))}
          </React.Fragment>
        ))}

        {columns.length > 0 && (
          <>
            <div
              style={{
                padding: "12px 16px",
                borderTop: "1px solid var(--sl-border-secondary)",
                position: "sticky",
                left: 0,
                background: "var(--sl-bg-secondary)",
                zIndex: 1,
                fontSize: 12,
                lineHeight: "18px",
                color: "var(--sl-text-quaternary)",
              }}
            >
              Scheduled
            </div>
            {columns.map((column) => (
              <div
                key={column.id}
                className="d-flex flex-column"
                style={{
                  padding: 12,
                  borderTop: "1px solid var(--sl-border-secondary)",
                  borderLeft: "1px solid var(--sl-border-secondary)",
                  background: "var(--sl-bg-secondary)",
                }}
              >
                <span
                  style={{
                    fontSize: 14,
                    lineHeight: "20px",
                    fontWeight: 600,
                    color: "var(--sl-text-primary)",
                  }}
                >
                  {hoursLabel(column.scheduled_minutes || 0)} h
                </span>
                {permissions.readWages && column.scheduled_cost != null && (
                  <span
                    style={{
                      fontSize: 12,
                      lineHeight: "18px",
                      color: "var(--sl-text-tertiary)",
                    }}
                  >
                    {column.day ? (
                      wholeDollars(column.scheduled_cost)
                    ) : (
                      <>
                        {wholeDollars(column.scheduled_cost)} ·{" "}
                        {percentLabel(column.labor_pct)} of forecast
                        {column.budget_pct != null && (
                          <span style={column.budget_pct > 100 ? { color: "var(--sl-text-warning)", fontWeight: 600 } : undefined}>
                            {" "}· {percentLabel(column.budget_pct)} of budget
                          </span>
                        )}
                      </>
                    )}
                  </span>
                )}
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

ScheduleGrid.propTypes = {
  view: PropTypes.oneOf(["position", "staff"]).isRequired,
  narrow: PropTypes.bool,
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  room: PropTypes.number,
  onOpenShift: PropTypes.func.isRequired,
  onAddShift: PropTypes.func.isRequired,
  cancellingEventId: PropTypes.number,
  onCancelShifts: PropTypes.func.isRequired,
  changingTimesEventId: PropTypes.number,
  onChangeShowTimes: PropTypes.func.isRequired,
};

export default ScheduleGrid;
